import { useEffect, useState } from "react";
import { useCreateShortLinkMutation } from "@/hooks/ReactQuery/ShortLinkReactQuery/ShortLinkReactQuery";
import { buildUrlWithUtm, copyShortUrl, EMPTY_UTM, MAX_TITLE_LENGTH } from "../utils";
import { FgGroupField } from "./FgGroups";
import { FgSwitch } from "./FgSwitch";
import { FgUtmFields } from "./FgUtmFields";
import { CopyIcon, QrIcon } from "./Icons";

export function FgCreateForm({ onOpenQr, groups, defaultGroup }) {
    const createShortLinkMutation = useCreateShortLinkMutation();

    const [url, setUrl] = useState('');
    const [title, setTitle] = useState('');
    const [group, setGroup] = useState(defaultGroup);
    const [utm, setUtm] = useState(EMPTY_UTM);
    const [openInApp, setOpenInApp] = useState(true);
    const [created, setCreated] = useState(null);

    // 목록에서 보고 있는 그룹에 새 링크를 넣는다
    useEffect(() => setGroup(defaultGroup), [defaultGroup]);

    const finalUrl = url.trim() ? buildUrlWithUtm(url, utm) : null;

    const handleSubmit = (e) => {
        e.preventDefault();

        createShortLinkMutation.mutate({ body: { url: finalUrl, title, group, openInApp } }, {
            onSuccess: (data) => {
                setCreated(data);
                setUrl('');
                setTitle('');
            },
        });
    }

    return (
        <section className="rounded-[15px] border border-gray-100 shadow-[var(--defaultBoxShadow)] bg-white p-5 min-[993px]:p-7 flex flex-col gap-5">
            <div>
                <h2 className="text-xl font-bold">단축 URL 만들기</h2>
                <p className="text-sm text-gray-500 mt-1">긴 링크를 <b className="text-gray-700">l.shool.uk</b> 짧은 주소로 바꾸고 클릭 수를 확인하세요.</p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-2 min-[600px]:flex-row">
                    <label className="flex-1 min-w-0 flex items-center gap-2 rounded-xl border border-gray-300 px-4 focus-within:border-[var(--mainColor)] focus-within:ring-2 focus-within:ring-[var(--mainColor300)]">
                        <img src="/assets/icons/link_default_000000.svg" alt="" className="w-5 h-5 opacity-40" />
                        <input
                            type="url"
                            required
                            maxLength={2048}
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="줄일 링크를 붙여넣으세요 (https://...)"
                            className="flex-1 min-w-0 py-3.5 text-[15px] outline-none bg-transparent"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={!finalUrl}
                        className="shrink-0 rounded-xl bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 px-6 py-3.5 text-[15px] font-bold text-white"
                    >
                        줄이기
                    </button>
                </div>

                <label className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-gray-600">라벨 <span className="font-normal text-gray-400">선택 · 목록에서 구분하기 위한 이름</span></span>
                    <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={MAX_TITLE_LENGTH}
                        placeholder="예: 10월 가을 세일 - 인스타 바이오"
                        className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-[var(--mainColor)]"
                    />
                </label>

                <FgGroupField value={group} onChange={setGroup} groups={groups} />

                <FgUtmFields utm={utm} onChange={setUtm} finalUrl={finalUrl} />

                <div className="flex items-center justify-between gap-4">
                    <div>
                        <p className="text-sm font-semibold">인앱 브라우저에서 앱으로 열기</p>
                        <p className="text-xs text-gray-500 mt-0.5">인스타·카톡에서 누르면 스마트스토어·블로그는 네이버 앱, 유튜브는 유튜브 앱으로 엽니다.</p>
                    </div>
                    <FgSwitch checked={openInApp} onChange={setOpenInApp} label="인앱 브라우저에서 앱으로 열기" />
                </div>
            </form>

            {created &&
                <div className="rounded-xl border-2 border-[var(--mainColor500)] bg-[var(--mainColor100)] p-4 flex flex-col gap-3 min-[600px]:flex-row min-[600px]:items-center">
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-[var(--mainColor)]">{created.title ? `"${created.title}" ` : ''}단축 URL 이 만들어졌어요</p>
                        <p className="text-lg font-bold break-all">{created.shortUrl}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <button type="button" onClick={() => copyShortUrl(created.shortUrl)} className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white">
                            <CopyIcon /> 복사
                        </button>
                        <button type="button" onClick={() => onOpenQr(created)} className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold">
                            <QrIcon /> QR
                        </button>
                    </div>
                </div>
            }
        </section>
    );
}
